import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { faqService } from "@/services/faqService";
import { useHotel } from "@/hooks/useHotel";
import {
  FAQFilters,
  CreateFAQDto,
  UpdateFAQDto,
  ReorderDto,
} from "@/services/interfaces/IFAQService";

export const faqKeys = {
  all: ["faqs"] as const,
  lists: () => [...faqKeys.all, "list"] as const,
  list: (filters?: Partial<FAQFilters>) =>
    [...faqKeys.lists(), filters] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;
const MOBILE_SETTINGS_QUERY_KEY = ["mobile-settings"] as const;

const invalidateFaqRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: faqKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: MOBILE_SETTINGS_QUERY_KEY });
};

export const useFAQs = (filters: Omit<FAQFilters, "hotelId">) => {
  const { hotelId } = useHotel();

  return useQuery({
    queryKey: faqKeys.list({ ...filters, hotelId }),
    queryFn: () =>
      faqService.getAll({ ...filters, hotelId: hotelId || "" } as FAQFilters),
    enabled: !!filters?.categoryId && !!hotelId,
  });
};

export const useCreateFAQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFAQDto) => faqService.create(data),
    onSuccess: () => {
      invalidateFaqRelatedQueries(queryClient);
    },
  });
};

export const useUpdateFAQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFAQDto }) =>
      faqService.update(id, data),
    onSuccess: () => {
      invalidateFaqRelatedQueries(queryClient);
    },
  });
};

export const useDeleteFAQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => faqService.deleteFAQ(id),
    onSuccess: () => {
      invalidateFaqRelatedQueries(queryClient);
    },
  });
};

export const useReorderFAQs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderDto) => faqService.reorder(data),
    onSuccess: () => {
      invalidateFaqRelatedQueries(queryClient);
    },
  });
};
